import { VacationCalendar, type CalendarEntry, type CalendarHoliday } from '@/components/features/Vacations/VacationCalendar';
import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type BreadcrumbItem } from '@/types';
import { Head, router, useForm } from '@inertiajs/react';
import { Download, FileSpreadsheet, Plus } from 'lucide-react';
import { FormEventHandler, useState } from 'react';
import { route } from 'ziggy-js';

interface LeaveTypeOption {
    id: string;
    name: string;
    slug: string;
}

interface MyRequest {
    id: string;
    number: string;
    leave_type: string | null;
    start_date: string;
    end_date: string;
    days: number;
    working_days: number;
    note: string | null;
    status: string;
    payout_timing: string | null;
    decision_note: string | null;
    approver: string | null;
    created_at: string | null;
    can_cancel: boolean;
    can_download_pdf: boolean;
}

interface PendingApproval {
    id: string;
    number: string;
    employee: string | null;
    leave_type: string | null;
    start_date: string;
    end_date: string;
    days: number;
    working_days: number;
    note: string | null;
    status: string;
}

interface ApprovalStep {
    approver: string | null;
    decision: string;
    decided_at: string | null;
}

interface LogRequest {
    id: string;
    number: string;
    employee: string | null;
    position: string | null;
    leave_type: string | null;
    start_date: string;
    end_date: string;
    working_days: number;
    status: string;
    submitted: string | null;
    current_approver: string | null;
    approvals: ApprovalStep[];
    can_download_pdf: boolean;
}

interface VacationsPageProps {
    myRequests: MyRequest[];
    pendingApprovals: PendingApproval[];
    leaveTypes: LeaveTypeOption[];
    calendar: CalendarEntry[];
    holidays: CalendarHoliday[];
    payoutTimingDefault: string;
    canViewLog: boolean;
    canExport: boolean;
    allRequests: LogRequest[];
}

const STATUS_VARIANT: Record<string, 'secondary' | 'default' | 'destructive' | 'outline'> = {
    pending: 'secondary',
    approved: 'default',
    rejected: 'destructive',
    cancelled: 'outline',
};

export default function VacationsIndex({
    myRequests,
    pendingApprovals,
    leaveTypes,
    calendar,
    holidays,
    payoutTimingDefault,
    canViewLog,
    canExport,
    allRequests,
}: VacationsPageProps) {
    const { t } = useTranslations();
    const [dialogOpen, setDialogOpen] = useState(false);

    const breadcrumbs: BreadcrumbItem[] = [{ title: t('vacations.nav'), href: '/vacations' }];

    const form = useForm({
        leave_type_id: '',
        start_date: '',
        end_date: '',
        note: '',
        payout_timing: payoutTimingDefault,
    });

    const submit: FormEventHandler = (e) => {
        e.preventDefault();
        form.post(route('vacations.store'), {
            preserveScroll: true,
            onSuccess: () => {
                form.reset();
                setDialogOpen(false);
            },
        });
    };

    const statusBadge = (status: string) => <Badge variant={STATUS_VARIANT[status] ?? 'secondary'}>{t(`vacations.statuses.${status}`)}</Badge>;

    const dateRange = (start: string, end: string) => `${start} → ${end}`;

    const cancelRequest = (id: string) => {
        if (!window.confirm(t('vacations.my_requests.cancel_confirm'))) return;
        router.post(route('vacations.cancel', id), {}, { preserveScroll: true });
    };

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('vacations.title')} />
            <PageHeader
                title={t('vacations.title')}
                subtitle={t('vacations.subtitle')}
                actions={
                    <>
                        {canExport && (
                            <Button asChild variant="outline">
                                <a href={route('vacations.export')}>
                                    <FileSpreadsheet className="mr-1 h-4 w-4" />
                                    {t('vacations.export.button')}
                                </a>
                            </Button>
                        )}
                        <ResponsiveModal open={dialogOpen} onOpenChange={setDialogOpen} variant="sheet">
                            <ResponsiveModalTrigger asChild>
                                <Button>
                                    <Plus className="mr-1 h-4 w-4" />
                                    {t('vacations.request.new')}
                                </Button>
                            </ResponsiveModalTrigger>
                            <ResponsiveModalContent size="lg">
                                <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
                                    <ResponsiveModalHeader>
                                        <ResponsiveModalTitle>{t('vacations.request.heading')}</ResponsiveModalTitle>
                                        <ResponsiveModalDescription>{t('vacations.subtitle')}</ResponsiveModalDescription>
                                    </ResponsiveModalHeader>

                                    <ResponsiveModalBody className="space-y-4">
                                        <div className="space-y-1">
                                            <Label htmlFor="leave_type_id">{t('vacations.request.leave_type')}</Label>
                                            <Select value={form.data.leave_type_id} onValueChange={(v) => form.setData('leave_type_id', v)}>
                                                <SelectTrigger id="leave_type_id" className="w-full">
                                                    <SelectValue placeholder={t('vacations.request.leave_type_placeholder')} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {leaveTypes.map((type) => (
                                                        <SelectItem key={type.id} value={type.id}>
                                                            {type.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {form.errors.leave_type_id && <p className="text-destructive text-xs">{form.errors.leave_type_id}</p>}
                                        </div>

                                        <div className="space-y-1">
                                            <Label htmlFor="start_date">{t('vacations.request.start_date')}</Label>
                                            <DatePicker
                                                value={form.data.start_date}
                                                onValueChange={(v) => form.setData('start_date', v)}
                                                placeholder={t('vacations.request.start_date')}
                                                className="w-full"
                                            />
                                            {form.errors.start_date && <p className="text-destructive text-xs">{form.errors.start_date}</p>}
                                        </div>
                                        <div className="space-y-1">
                                            <Label htmlFor="end_date">{t('vacations.request.end_date')}</Label>
                                            <DatePicker
                                                value={form.data.end_date}
                                                onValueChange={(v) => form.setData('end_date', v)}
                                                placeholder={t('vacations.request.end_date')}
                                                className="w-full"
                                            />
                                            {form.errors.end_date && <p className="text-destructive text-xs">{form.errors.end_date}</p>}
                                        </div>

                                        <div className="space-y-1">
                                            <Label htmlFor="note">{t('vacations.request.note_optional')}</Label>
                                            <Textarea
                                                id="note"
                                                className="w-full"
                                                value={form.data.note}
                                                onChange={(e) => form.setData('note', e.target.value)}
                                                placeholder={t('vacations.request.note_placeholder')}
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <Label htmlFor="payout_timing">{t('vacations.request.payout_timing')}</Label>
                                            <Select value={form.data.payout_timing} onValueChange={(v) => form.setData('payout_timing', v)}>
                                                <SelectTrigger id="payout_timing" className="w-full">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="three_days_before">
                                                        {t('vacations.request.payout_three_days_before')}
                                                    </SelectItem>
                                                    <SelectItem value="with_salary">{t('vacations.request.payout_with_salary')}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </ResponsiveModalBody>

                                    <ResponsiveModalFooter>
                                        <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                            {t('vacations.request.cancel')}
                                        </Button>
                                        <Button type="submit" disabled={form.processing}>
                                            {t('vacations.request.submit')}
                                        </Button>
                                    </ResponsiveModalFooter>
                                </form>
                            </ResponsiveModalContent>
                        </ResponsiveModal>
                    </>
                }
            />

            <PageContent>
                <Tabs defaultValue="calendar" className="w-full">
                    <TabsList>
                        <TabsTrigger value="calendar">{t('vacations.tabs.calendar')}</TabsTrigger>
                        <TabsTrigger value="my_requests">{t('vacations.tabs.my_requests')}</TabsTrigger>
                        <TabsTrigger value="approvals">
                            {t('vacations.tabs.approvals')}
                            {pendingApprovals.length > 0 && <Badge className="ml-2">{pendingApprovals.length}</Badge>}
                        </TabsTrigger>
                        {canViewLog && <TabsTrigger value="log">{t('vacations.tabs.log')}</TabsTrigger>}
                    </TabsList>

                    <TabsContent value="calendar" className="mt-4">
                        <VacationCalendar initialEntries={calendar} initialHolidays={holidays} />
                    </TabsContent>

                    <TabsContent value="my_requests" className="mt-4">
                        {myRequests.length === 0 ? (
                            <p className="text-muted-foreground py-8 text-center text-sm">{t('vacations.my_requests.empty')}</p>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('vacations.columns.number')}</TableHead>
                                        <TableHead>{t('vacations.columns.type')}</TableHead>
                                        <TableHead>{t('vacations.columns.dates')}</TableHead>
                                        <TableHead>{t('vacations.columns.working_days')}</TableHead>
                                        <TableHead>{t('vacations.columns.status')}</TableHead>
                                        <TableHead>{t('vacations.columns.approver')}</TableHead>
                                        <TableHead className="text-right">{t('vacations.columns.actions')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {myRequests.map((request) => (
                                        <TableRow key={request.id}>
                                            <TableCell className="font-mono text-xs">{request.number}</TableCell>
                                            <TableCell>{request.leave_type}</TableCell>
                                            <TableCell>{dateRange(request.start_date, request.end_date)}</TableCell>
                                            <TableCell>{request.working_days}</TableCell>
                                            <TableCell>{statusBadge(request.status)}</TableCell>
                                            <TableCell>{request.approver ?? '—'}</TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    {request.can_download_pdf && (
                                                        <Button asChild variant="outline" size="sm">
                                                            <a href={route('vacations.pdf', request.id)} target="_blank" rel="noopener noreferrer">
                                                                <Download className="mr-1 h-3 w-3" />
                                                                {t('vacations.my_requests.download_pdf')}
                                                            </a>
                                                        </Button>
                                                    )}
                                                    {request.can_cancel && (
                                                        <Button variant="ghost" size="sm" onClick={() => cancelRequest(request.id)}>
                                                            {t('vacations.my_requests.cancel')}
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </TabsContent>

                    <TabsContent value="approvals" className="mt-4">
                        {pendingApprovals.length === 0 ? (
                            <p className="text-muted-foreground py-8 text-center text-sm">{t('vacations.approvals.empty')}</p>
                        ) : (
                            <div className="space-y-3">
                                {pendingApprovals.map((request) => (
                                    <ApprovalCard key={request.id} request={request} />
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {canViewLog && (
                        <TabsContent value="log" className="mt-4">
                            {allRequests.length === 0 ? (
                                <p className="text-muted-foreground py-8 text-center text-sm">{t('vacations.log.empty')}</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('vacations.columns.number')}</TableHead>
                                                <TableHead>{t('vacations.columns.employee')}</TableHead>
                                                <TableHead>{t('vacations.columns.position')}</TableHead>
                                                <TableHead>{t('vacations.columns.type')}</TableHead>
                                                <TableHead>{t('vacations.columns.dates')}</TableHead>
                                                <TableHead>{t('vacations.columns.working_days')}</TableHead>
                                                <TableHead>{t('vacations.columns.submitted')}</TableHead>
                                                <TableHead>{t('vacations.columns.status')}</TableHead>
                                                <TableHead>{t('vacations.log.awaiting')}</TableHead>
                                                <TableHead>{t('vacations.log.approvals')}</TableHead>
                                                <TableHead className="text-right">{t('vacations.columns.actions')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {allRequests.map((request) => (
                                                <TableRow key={request.id}>
                                                    <TableCell className="font-mono text-xs">{request.number}</TableCell>
                                                    <TableCell>{request.employee}</TableCell>
                                                    <TableCell className="text-muted-foreground">{request.position ?? '—'}</TableCell>
                                                    <TableCell>{request.leave_type}</TableCell>
                                                    <TableCell>{dateRange(request.start_date, request.end_date)}</TableCell>
                                                    <TableCell>{request.working_days}</TableCell>
                                                    <TableCell>{request.submitted}</TableCell>
                                                    <TableCell>{statusBadge(request.status)}</TableCell>
                                                    <TableCell className="text-muted-foreground text-xs">
                                                        {request.status === 'pending' ? (request.current_approver ?? '—') : '—'}
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground text-xs">
                                                        {request.approvals.length === 0
                                                            ? '—'
                                                            : request.approvals.map((a) => `${a.approver ?? ''} (${a.decided_at ?? ''})`).join('; ')}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {request.can_download_pdf && (
                                                            <Button asChild variant="outline" size="sm">
                                                                <a
                                                                    href={route('vacations.pdf', request.id)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    <Download className="mr-1 h-3 w-3" />
                                                                    {t('vacations.my_requests.download_pdf')}
                                                                </a>
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </TabsContent>
                    )}
                </Tabs>
            </PageContent>
        </AppLayout>
    );
}

function ApprovalCard({ request }: { request: PendingApproval }) {
    const { t } = useTranslations();
    const [note, setNote] = useState('');

    const decide = (action: 'approve' | 'reject') => {
        const confirmKey = action === 'approve' ? 'vacations.approvals.approve_confirm' : 'vacations.approvals.reject_confirm';
        if (!window.confirm(t(confirmKey))) return;
        router.post(route(`vacations.${action}`, request.id), { decision_note: note }, { preserveScroll: true });
    };

    return (
        <div className="rounded-lg border p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="font-medium">{request.employee}</div>
                    <div className="text-muted-foreground text-sm">
                        <span className="font-mono">{request.number}</span> · {request.leave_type} · {request.start_date} → {request.end_date} ·{' '}
                        {request.working_days} {t('vacations.request.working_days')}
                    </div>
                    {request.note && <p className="mt-2 text-sm">{request.note}</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                    <Input
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder={t('vacations.approvals.decision_note')}
                        className="w-64"
                    />
                    <div className="flex gap-2">
                        <Button size="sm" onClick={() => decide('approve')}>
                            {t('vacations.approvals.approve')}
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => decide('reject')}>
                            {t('vacations.approvals.reject')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
